import { useEffect, useState } from 'react';
import { GOAL_STEPS, WEIGHT_UNIT } from '../../../types/enum';
import { convertWeight } from '../../../lib/utils';
import request from '../../../lib/request';

const calculateWeeksToAchieveWeight = ({
  currentWeight,
  currentCalorie,
  goalWeight,
  goalCalorie,
  unit,
}) => {
  let weeks = 0;
  let weight = currentWeight;
  let stepWeight = 0;

  let subCalorie = goalCalorie - Math.floor(currentCalorie);
  if (subCalorie > 0) {
    while (subCalorie > 0) {
      subCalorie -= 250;
      stepWeight += unit === WEIGHT_UNIT.LB ? 0.55 : 0.25;
    }
  }
  if (subCalorie < 0) {
    while (subCalorie < 0) {
      subCalorie += 250;
      stepWeight += unit === WEIGHT_UNIT.LB ? 0.55 : 0.25;
    }
  }

  if (weight > goalWeight) {
    while (weight > goalWeight) {
      weight -= stepWeight;
      weeks++;
    }
  }
  if (weight < goalWeight) {
    while (weight < goalWeight) {
      weight += stepWeight;
      weeks++;
    }
  }

  return weeks;
};

const Summary = ({ goals, setCurrentStep, userDetail, weightUnit }) => {
  const [weeks, setWeeks] = useState(0);
  const [macronutrients, setMacronutrients] = useState({
    protein: { calories: 0, grams: 0 },
    carbs: { calories: 0, grams: 0 },
    fat: { calories: 0, grams: 0 },
  });

  useEffect(() => {
    const result = calculateWeeksToAchieveWeight({
      currentWeight: userDetail.weight,
      currentCalorie: goals.TDEE,
      goalWeight: Number(goals.weight),
      goalCalorie: goals.ownCalorie ? goals.ownCalorie : goals.dailyCalorie,
      unit: weightUnit,
    });
    setWeeks(result);

    // Calculate macronutrients
    const totalCalories = goals.ownCalorie || goals.dailyCalorie;
    const { protein, carbs, fat } = goals.macronutrientSplit;

    const proteinCalories = (totalCalories * protein) / 100;
    const carbsCalories = (totalCalories * carbs) / 100;
    const fatCalories = (totalCalories * fat) / 100;

    setMacronutrients({
      protein: {
        calories: Math.round(proteinCalories),
        grams: Math.round(proteinCalories / 4),
      },
      carbs: {
        calories: Math.round(carbsCalories),
        grams: Math.round(carbsCalories / 4),
      },
      fat: {
        calories: Math.round(fatCalories),
        grams: Math.round(fatCalories / 9),
      },
    });
  }, [goals]);

  const handleNext = async () => {
    try {
      await saveGoals();
      setCurrentStep(GOAL_STEPS.DONE);
      window.location.href = '/';
    } catch (err) {
      alert(err.message);
    }
  };

  const saveGoals = async () => {
    try {
      await request.post('/signup/goal', {
        userId: userDetail.userId,
        goal: goals.endGoal,
        weight:
          weightUnit === WEIGHT_UNIT.LB
            ? Math.ceil(
                convertWeight(
                  goals.weight ?? userDetail.weight,
                  WEIGHT_UNIT.LB,
                  WEIGHT_UNIT.KG
                )
              )
            : Number(goals.weight ?? userDetail.weight),
        dailyCalorie: goals.ownCalorie ? goals.ownCalorie : goals.dailyCalorie,
        dailyWorkoutHours: Number(goals.workoutHours),
        weeklyWorkoutDays: Number(goals.workoutDays),
        weeks,
        dailyProtein: macronutrients.protein.grams,
        dailyCarbs: macronutrients.carbs.grams,
        dailyFat: macronutrients.fat.grams,
      });
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div>
      <div className='bg-white/10 rounded-xl p-4 shadow-sm'>
        <h1 className='text-xl font-bold mb-4 text-white'>Goal Summary</h1>
        <div className='space-y-4'>
          <div className='flex items-center justify-between'>
            <span className='text-white/80'>Daily Calorie Target</span>
            <span className='bg-yellow-500 text-black px-2 py-1 rounded-full font-bold'>
              {goals.ownCalorie
                ? goals.ownCalorie.toLocaleString()
                : goals.dailyCalorie.toLocaleString()}{' '}
              Cal/day
            </span>
          </div>

          {/* Macronutrient Display Section */}
          <div className='space-y-3'>
            <h2 className='text-white font-semibold'>Daily Macronutrients</h2>
            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <div className='text-white/80'>
                  Protein
                  <br />
                  <span className='text-white/60 text-xs'>
                    {macronutrients.protein.calories} calories
                  </span>
                </div>
                <span className='text-white font-bold'>
                  {macronutrients.protein.grams}g
                </span>
              </div>
              <div className='flex items-center justify-between'>
                <div className='text-white/80'>
                  Carbohydrates
                  <br />
                  <span className='text-white/60 text-xs'>
                    {macronutrients.carbs.calories} calories
                  </span>
                </div>
                <span className='text-white font-bold'>
                  {macronutrients.carbs.grams}g
                </span>
              </div>
              <div className='flex items-center justify-between'>
                <div className='text-white/80'>
                  Fat
                  <br />
                  <span className='text-white/60 text-xs'>
                    {macronutrients.fat.calories} calories
                  </span>
                </div>
                <span className='text-white font-bold'>
                  {macronutrients.fat.grams}g
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {goals.endGoal !== 'Maintain' && (
        <div className='bg-white/10 rounded-xl p-4 shadow-sm mt-4'>
          <h1 className='text-xl font-bold mb-4 text-white'>
            Timeline Projection
          </h1>
          <div className='flex items-center space-x-2'>
            <span className='text-white/80'>From</span>
            <span className='font-semibold text-white'>
              {userDetail.weight} kg
            </span>
            <span className='text-white/80'>to</span>
            <span className='font-semibold text-white'>{goals.weight} kg</span>
            <span className='text-white/80'>in</span>
            <span className='bg-yellow-500 text-black px-2 py-1 rounded-full font-bold'>
              {weeks} weeks
            </span>
          </div>
        </div>
      )}

      <button
        className='w-full bg-yellow-500 text-black font-bold rounded-full transition-colors duration-200 py-3 mt-5 hover:bg-yellow-600'
        onClick={handleNext}
      >
        Sign in and Let's Start
      </button>
    </div>
  );
};

export default Summary;
